import { useCallback, useSyncExternalStore } from 'react';

/**
 * `matchMedia` como estado de React.
 *
 * Usa `useSyncExternalStore`, que es exactamente para esto: suscribirse a algo
 * de fuera de React. La alternativa (useState + useEffect que hace setState en
 * el cuerpo) provoca renders en cascada y un primer frame con el valor
 * equivocado — justo el parpadeo que se ve al cargar con el menu movil.
 * Fuera del navegador devuelve `false` sin romper.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => undefined;
      }
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  }, [query]);

  // En servidor no hay media queries: se asume "no coincide".
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
